import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';

const renderStatValue = (value) => {
  const plusIdx = value.lastIndexOf('+');
  if (plusIdx > 0) {
    return (
      <>
        <Box component="span" sx={{ fontWeight: 800 }}>{value.slice(0, plusIdx)}</Box>
        <Box component="span" sx={{ fontWeight: 300 }}>{value.slice(plusIdx)}</Box>
      </>
    );
  }
  return <Box component="span" sx={{ fontWeight: 800 }}>{value}</Box>;
};

const OurImpact = () => {
  const home = useSelector((state) => state.cms.home);
  const cms = useMemo(() => {
    const sections = Array.isArray(home?.data) ? home.data : [];
    const section = sections.find((s) => s?.id === 64);
    const related = Array.isArray(section?.relatedCMS) ? section.relatedCMS : [];
    const stats = (related.filter(r => r && (r.status === 1 || r.status === true)) || []).map(r => ({
      value: r.description || '',
      label: r.sub_title || '',
    })).filter(s => s.value && /\d/.test(s.value));
    return {
      title: section?.title || '',
      description: section?.description || '',
      image: section?.image || '',
      stats,
    };
  }, [home]);

  const paddingByIndex = [
    { xs: '15px', lg: '0 0 36px 12px' },
    { xs: '15px', lg: '0 0 30px 40px' },
    { xs: '15px', lg: '30px 0 0 12px' },
    { xs: '15px', lg: '30px 0 0 40px' },
  ];

  return (
    <section style={{ marginTop: '40px', marginBottom: '15px' }}>
      <Box
        sx={{
          width: '94.44%',
          maxWidth: '1440px',
          borderRadius: { xs: '20px', sm: '25px', md: '34px' },
          backgroundColor: '#1D1D1B',
          margin: '0 auto',
          overflow: 'hidden',
          boxSizing: 'border-box',
          '@media (max-width: 1199px)': { display: 'flex', flexDirection: 'column', minHeight: 'auto' },
          '@media (min-width: 1200px)': { display: 'flex', flexDirection: 'row', minHeight: '610px' },
        }}
      >
        {/* Text + Stats panel */}
        <Box
          sx={{
            width: { xs: '100%', lg: '50%' },
            padding: { xs: '30px 18px 30px 13px', sm: '60px 40px', lg: '80px 60px' },
            order: { xs: 2, lg: 1 },
            '@media (min-width: 900px) and (max-width: 1199px)': { order: 2 },
          }}
        >
          {/* Title */}
          <Box
            sx={{
              fontFamily: 'Red Hat Display',
              fontWeight: 800,
              fontSize: { xs: '28px', sm: '32px', md: '36px' },
              lineHeight: '100%',
              textTransform: 'capitalize',
              color: '#FFFFFF',
              marginBottom: { xs: '12px', md: '16px' },
            }}
          >
            {cms.title}
          </Box>

          {/* Description */}
          <Box
            sx={{
              fontFamily: 'Red Hat Display',
              fontWeight: 400,
              fontSize: { xs: '12px', sm: '15px', md: '16px' },
              lineHeight: { xs: '24px', sm: '26px', md: '28px' },
              color: 'rgba(255,255,255,0.8)',
              marginBottom: { xs: '20px', sm: '50px', md: '68px' },
              '@media (min-width: 1220px)': { maxWidth: '517px' },
            }}
          >
            {cms.description}
          </Box>

          {/* Statistics Grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', lg: '259px 259px' },
              gridTemplateRows: { xs: 'auto auto', lg: '106px 106px' },
              maxWidth: { xs: '100%', lg: '519px' },
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '50%',
                left: '0',
                right: '0',
                height: '1px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                zIndex: 1,
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                left: '50%',
                top: '0',
                bottom: '0',
                width: '1px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                zIndex: 1,
              },
            }}
          >
            {cms.stats.map((stat, idx) => {
              const isGreen = idx === 0 || idx === 3;
              return (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    position: 'relative',
                    padding: paddingByIndex[idx] || { xs: '15px', lg: '15px' },
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: 'Red Hat Display',
                      fontSize: { xs: '32px', sm: '38px', md: '45.33px' },
                      lineHeight: { xs: '40px', sm: '46px', md: '55.63px' },
                      color: isGreen ? '#A9C74E' : '#FFFFFF',
                    }}
                  >
                    {renderStatValue(stat.value)}
                  </Box>
                  <Box
                    sx={{
                      fontFamily: 'Red Hat Display',
                      fontWeight: 600,
                      fontSize: { xs: '11px', sm: '14px', md: '15px' },
                      lineHeight: { xs: '20px', md: '26px' },
                      color: '#FFFFFF',
                      marginTop: { xs: '3px', sm: '5px', md: '8px' },
                    }}
                  >
                    {stat.label}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Image panel */}
        <Box
          sx={{
            width: { xs: '100%', lg: '50%' },
            minHeight: { xs: '250px', sm: '550px', lg: '610px' },
            position: 'relative',
            order: { xs: 1, lg: 2 },
            '@media (min-width: 900px) and (max-width: 1199px)': { order: 1 },
          }}
        >
          {cms.image && (
            <img
              src={cms.image}
              alt="Our Impact"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                position: 'absolute',
                top: 0,
                left: 0,
              }}
            />
          )}
        </Box>
      </Box>
    </section>
  );
};

export default OurImpact;
